import React, { useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import $ from 'jquery';
import { ActionButton } from '@bingads-webui-react/fabric-control';
import { AdPlacementsGridPreviewPanel } from '@bingads-webui-campaign-react/ad-placements-grid-preview';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getAdCreatorInstrumentation } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { ConditionalTooltipHost } from '@bingads-webui-campaign-react/conditional-tooltip-host';
import {
  attachAIGenAssetContextualAttribute,
} from '@bingads-webui-campaign/responsive-search-ad-strength';
import { isPlayPausePreviewImageEnabled } from '@bingads-webui-campaign-react/play-pause-buttons';

import {
  useImageExperienceAdPreviewModel,
  AdPreviewCarouselLayout,
  AdPreviewSinglePlacementLayout,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

import C from '../constants';
import getGenericImageObjectAsync from '../util/get-generic-image-object';
import getCroppedImage from '../util/get-cropped-image';
import { getPreviewCarouselStyle } from '../../style/js/mma-msan-preview-carousel-style';
import { ImageExperienceImage } from '../model/image-experience-image';

import {
  getPlacementTitle as getPlacementTitleImpl,
  getPlacementTitleInViewAll,
} from '../ad-preview-library-wrapper/util';
import { getOnRenderPlacement } from './unified-placement-render';

export const isInvalidImageSize = (image, minSize) => {
  if (!image) {
    return false;
  }

  let widthToCheck = image.Width;
  let heightToCheck = image.Height;

  if (image.BaseImage
    && image.BaseImage.CropSettings
    && image.BaseImage.CropSettings.SourceWidth > 0
    && image.BaseImage.CropSettings.SourceHeight > 0) {
    widthToCheck = image.BaseImage.CropSettings.SourceWidth;
    heightToCheck = image.BaseImage.CropSettings.SourceHeight;
  }

  return image && minSize && (widthToCheck < minSize[0] || heightToCheck < minSize[1]);
};

export const UnStyledMMAMSANPreviewCarousel = ({
  adPreviewConfig,

  imageList,
  genericImage,
  logo,
  totalImageListCount,
  previewTexts,

  enableAutoRotate,
  disablePlacementActions,
  isAdCreatorEnabled,
  isMMAV2Enabled,
  isMSANBoostEnabled,
  isBoost200x100ImageEnabled,
  isBoostNewAspectRatioSupportEnabled,
  isOnlyOneIncludedImage,
  hotspots, // for boost Windows Spotlight placement

  onAddAspectRatio: onAddAspectRatioProp,
  onCropImage: onCropImageProp,
  onRemoveAspectRatio: onRemoveAspectRatioProp,
  onUsedViewAllPlacements,

  isImageFittingEnabled,

  activityLogger,
  permissions,
  instrumentation,
  adCreatorScenario,
  i18n,
  newI18n,
  classes,
}) => {
  // states
  const [activeIndex, setActiveIndex] = useState(0);
  const [defaultImages, setDefaultImages] = useState([]);
  const [isViewAllPanelOpen, setIsViewAllPanelOpen] = useState(false);

  useEffect(() => {
    activityLogger.signal('MMA: preview opened.', 'responsive ad editor');
  }, [activityLogger]);

  const aspectRatiosOrder = useMemo(() => {
    let theAspectRatiosOrder = isMMAV2Enabled ? C.audienceMMAV2AspectRatiosWithoutOriginalImage : C.multimediaAdinMSANRatios;
    if (isBoost200x100ImageEnabled || isBoostNewAspectRatioSupportEnabled) {
      theAspectRatiosOrder = C.boostMultimediaAdinMSANRatios;

      if (isBoostNewAspectRatioSupportEnabled) {
        theAspectRatiosOrder = C.boostMultimediaAdinMSANRatiosNewSupport;
      }
    }
    return theAspectRatiosOrder;
  }, [isBoost200x100ImageEnabled, isBoostNewAspectRatioSupportEnabled, isMMAV2Enabled]);

  const aspectRatios = useMemo(() => {
    const curAspectRatios = isMMAV2Enabled ?
      _.pick(C.aspectRatiosForMMAV2, C.audienceMMAV2AspectRatiosWithoutOriginalImage) :
      _.pick(C.aspectRatios, C.multimediaAdinMSANRatios);

    if (isBoost200x100ImageEnabled || isBoostNewAspectRatioSupportEnabled) {
      curAspectRatios.Image200x100 = C.aspectRatiosForBoost200x100.Image200x100;

      if (isBoostNewAspectRatioSupportEnabled) {
        _.extend(curAspectRatios, C.aspectRatiosForBoostNewSupport);
      }
    }
    return curAspectRatios;
  }, [isBoost200x100ImageEnabled, isBoostNewAspectRatioSupportEnabled, isMMAV2Enabled]);

  const isRecommendationTypeImproveMultiMediaAdsEnabled = true;

  const imageListForDisplay = useMemo(() => {
    let theImageListForDisplay = imageList && imageList.length > 0
      ? imageList : defaultImages;

    theImageListForDisplay = _.filter(theImageListForDisplay, image => image.Type !== 'OriginalImage');

    theImageListForDisplay = _.sortBy(
      theImageListForDisplay,
      image => aspectRatiosOrder.indexOf(image.Type)
    );

    if (isMMAV2Enabled) {
      theImageListForDisplay && _.each(theImageListForDisplay, (image) => {
        if (image && isInvalidImageSize(image, aspectRatios[image.type].minSize)) {
          image.setIncluded(false);
        }
      });
    }

    return theImageListForDisplay;
  }, [aspectRatios, aspectRatiosOrder, defaultImages, imageList, isMMAV2Enabled]);

  useEffect(() => {
    if (_.result(_.first(imageListForDisplay), 'isRecommendedImage')) {
      // Registers AI generated asset contextual attribute popup to listen for key/mouse events
      const element = $('.iconba.iconba-Lightbulb.new-preview-carousel');
      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n,
        improveMMAOption: { type: 'Image' },
        newI18n,
      }));
    }
  }, [i18n, imageListForDisplay, newI18n]);

  const setupImagesAsync = useCallback(() => {
    const imagesToSetup = [
      genericImage,
    ];
    const setupPromises =
      _.map(imagesToSetup, imageToSetup => getGenericImageObjectAsync(imageToSetup)
        .then((imageObj) => {
          const croppedImages = getCroppedImage({
            originalImageToCrop: imageObj,
            isMultimediaAdinMSANEnabled: true,
            isMMAV2Enabled,
            isBoost200x100ImageEnabled,
            isBoostNewAspectRatioSupportEnabled,
          });

          return croppedImages;
        }));

    Promise.all(setupPromises).then(([defaultImages1]) => {
      setDefaultImages(defaultImages1);
    });
  }, [genericImage, isBoost200x100ImageEnabled, isBoostNewAspectRatioSupportEnabled, isMMAV2Enabled]);

  useEffect(() => {
    if (_.isEmpty(defaultImages) && (!imageList || imageList.length === 0)) {
      setupImagesAsync();
    }
  }, [defaultImages, imageList, setupImagesAsync]);

  const {
    imageExperiencePlacementSets: placementSets,
    onUpdateImageExperienceImageList,
  } = useImageExperienceAdPreviewModel(adPreviewConfig.placementConfig, undefined, isImageFittingEnabled);

  const placementList = useMemo(() => {
    // should only have one placement set
    const list = _.chain(placementSets).map(placementSet => placementSet.placementList).flatten().value();
    return list;
  }, [placementSets]);

  useEffect(() => {
    // placeholder images are used in multi-images placements
    const newImageList = [imageListForDisplay];
    // make all images active
    onUpdateImageExperienceImageList(newImageList, null);
  }, [imageListForDisplay, onUpdateImageExperienceImageList]);

  const [onRenderPlacement, onRenderPlacementGridView] = useMemo(() => {
    const params = {
      previewTexts,
      i18n,
      logo,
      hotspots,
      opacityChangeEnabled: true,
    };
    return [
      getOnRenderPlacement(params, classes.placementContainerRoot),
      getOnRenderPlacement(params, classes.placementContainerRootGridView),
    ];
  }, [classes, hotspots, i18n, logo, previewTexts]);

  const getPlacementTitle = useCallback((placementIndex, isInViewAllPanel = false) => {
    if (isInViewAllPanel) {
      return getPlacementTitleInViewAll(adPreviewConfig, placementIndex, i18n);
    }
    return getPlacementTitleImpl(adPreviewConfig, placementIndex, i18n);
  }, [adPreviewConfig, i18n]);

  const getEditingImageIndex = useCallback(
    e => (_.isUndefined(e.target.dataset.index) ? activeIndex : parseInt(e.target.dataset.index, 10)),
    [activeIndex]
  );

  const onAddAspectRatio = useCallback((e) => {
    const index = getEditingImageIndex(e);

    activityLogger.signal('MMA: include button clicked', 'responsive ad editor');

    onAddAspectRatioProp(imageListForDisplay[index].type);
  }, [activityLogger, getEditingImageIndex, imageListForDisplay, onAddAspectRatioProp]);

  const onCrop = useCallback((e) => {
    const index = getEditingImageIndex(e);

    activityLogger.signal('MMA: re-crop clicked', 'responsive ad editor');

    if (isAdCreatorEnabled) {
      setIsViewAllPanelOpen(false);
      onCropImageProp(imageListForDisplay[index]);
    } else {
      onCropImageProp(imageListForDisplay[index]);
    }
  }, [activityLogger, getEditingImageIndex, imageListForDisplay, isAdCreatorEnabled, onCropImageProp]);

  const onRemoveAspectRatio = useCallback((e) => {
    const index = getEditingImageIndex(e);

    activityLogger.signal('MMA: exclude button clicked', 'responsive ad editor');

    onRemoveAspectRatioProp(imageListForDisplay[index].type);
  }, [activityLogger, getEditingImageIndex, imageListForDisplay, onRemoveAspectRatioProp]);

  const getAdPreviewTitle = useCallback(() => getPlacementTitle(activeIndex), [activeIndex, getPlacementTitle]);

  const isPlacementActionsEnabled = useMemo(
    () => totalImageListCount !== 0 && !disablePlacementActions,
    [disablePlacementActions, totalImageListCount]
  );

  const shouldDisableExcludeBtn = useCallback((selectedImage) => {
    const onlyOneIncluded =
      _.filter(imageListForDisplay, image => image.isIncluded, []).length === 1;

    return totalImageListCount === 1 ||
      onlyOneIncluded || isOnlyOneIncludedImage(selectedImage.type);
  }, [imageListForDisplay, isOnlyOneIncludedImage, totalImageListCount]);

  const getActionButtons = useCallback(({ isIncluded, index }) => {
    const selectedImage = imageListForDisplay[index];

    if (isIncluded) {
      const editButtonTextKey = isAdCreatorEnabled
        ? 'ImageExperience_Edit_Aspect_Ratio' : 'ImageEditor_CropImage';

      return (
        <React.Fragment>
          <button
            className="crop-image-button btn btn-default"
            onClick={onCrop}
            data-index={index}
          >
            {i18n.getString(editButtonTextKey)}
          </button>
          <ConditionalTooltipHost
            condition={isAdCreatorEnabled && shouldDisableExcludeBtn(selectedImage)}
            content={i18n.getString('ImageExperience_Image_Placement_Required_Message')}
            calloutProps={{ gapSpace: 8 }}
          >
            <button
              disabled={shouldDisableExcludeBtn(selectedImage)}
              className="exclude-aspect-ratio-button btn btn-default"
              onClick={onRemoveAspectRatio}
              data-index={index}
            >
              {i18n.getString('ImageEditor_Exclude_AspectRatio')}
            </button>
          </ConditionalTooltipHost>
        </React.Fragment>
      );
    }

    /* istanbul ignore next */
    return (
      <button
        className="include-aspect-ratio-button btn btn-default"
        onClick={onAddAspectRatio}
        data-index={index}
      >
        {i18n.getString('ImageEditor_Include_AspectRatio')}
      </button>
    );
  }, [i18n, imageListForDisplay, isAdCreatorEnabled, onAddAspectRatio, onCrop, onRemoveAspectRatio, shouldDisableExcludeBtn]);

  const getAdPreviewHeaderInternal = useCallback(({ title, index, enableActionBtn }) => {
    const selectedImage = imageListForDisplay[index];

    /* istanbul ignore if */
    if (!selectedImage) {
      return null;
    }

    const isSelectedImageIncluded = _.result(selectedImage, 'isIncluded');
    const minSize = selectedImage && aspectRatios[selectedImage.type].minSize;
    const hideActionBtn = !enableActionBtn
      || (!isSelectedImageIncluded && isInvalidImageSize(selectedImage, minSize));

    return (
      <div className="preview-header">
        <div className={classnames('ad-preview-title', 'with-additional-tag')}>
          {title}
        </div>
        <div className="flex-grow" />
        {!hideActionBtn &&
          <div className={classnames('ad-preview-actions', 'inline-title')}>
            {getActionButtons({
              isIncluded: isSelectedImageIncluded,
              index,
            })}
          </div>
        }
      </div>
    );
  }, [aspectRatios, getActionButtons, imageListForDisplay]);

  const getAdPreviewHeader = useCallback(({ enableActionBtn }) => {
    const title = getAdPreviewTitle();

    return isAdCreatorEnabled ?
      <React.Fragment>
        {
          getAdPreviewHeaderInternal({
            title,
            index: activeIndex,
            enableActionBtn,
          })
        }
      </React.Fragment> : (
        <div className="ad-preview-title">
          {title}
        </div>
      );
  }, [activeIndex, getAdPreviewHeaderInternal, getAdPreviewTitle, isAdCreatorEnabled]);

  const getValidationMessage = useCallback((image) => {
    const isSelectedImageIncluded = _.result(image, 'isIncluded');
    const minSize = image && aspectRatios[image.type].minSize;

    if (!isSelectedImageIncluded) {
      return isInvalidImageSize(image, minSize) &&
        <span className="action-label">
          {
            i18n.getString('ImageEditor_SizeValidationFailTemplate', {
              width: minSize[0],
              height: minSize[1],
            })
          }
        </span>;
    }

    return null;
  }, [aspectRatios, i18n]);

  const getAdPreviewActions = useCallback(() => {
    const selectedImage = imageListForDisplay[activeIndex];

    /* istanbul ignore if */
    if (!selectedImage) {
      return null;
    }

    const isSelectedImageIncluded = _.result(selectedImage, 'isIncluded');
    const hideActionButtons = isAdCreatorEnabled;
    const validationMsgElement = getValidationMessage(selectedImage);

    return (
      <div className="ad-preview-actions">
        {!isSelectedImageIncluded ?
          (
            <React.Fragment>
              {validationMsgElement}
              {!validationMsgElement && !hideActionButtons &&
                getActionButtons({ isIncluded: isSelectedImageIncluded, index: activeIndex })
              }
            </React.Fragment>
          ) :
          (!hideActionButtons
            && getActionButtons({ isIncluded: isSelectedImageIncluded, index: activeIndex }))
        }
      </div>
    );
  }, [activeIndex, getActionButtons, getValidationMessage, imageListForDisplay, isAdCreatorEnabled]);

  const enableActionBtn = isPlacementActionsEnabled;
  const enableViewAllBtn = isMSANBoostEnabled ? false : enableActionBtn && isAdCreatorEnabled;

  return (
    <div className={classnames('new-preview-carousel-container', isPlayPausePreviewImageEnabled(permissions) ? classes.PlayPauseButtonsContainer : null)}>
      {getAdPreviewHeader({ enableActionBtn })}
      {
        isRecommendationTypeImproveMultiMediaAdsEnabled
        && _.result(_.first(imageListForDisplay), 'isRecommendedImage') &&
        <div className="improve-mma-title-container">
          <span
            aria-label={i18n.getString('Ad_Strength_RSA_AI_Generated_Asset')}
            className="iconba iconba-Lightbulb new-preview-carousel"
          />
          <span>{newI18n.getString(_TL_('Recommended image'))}</span>
        </div>
      }
      {enableActionBtn && getAdPreviewActions()}
      <AdPreviewCarouselLayout
        placementList={placementList}
        onRenderPlacement={onRenderPlacement}
        enableAutoRotate={enableAutoRotate}
        startAutoRotating={!window.e2eTestEnvironment} // disable auto-rotate in test env
        i18n={i18n}
        onActiveIndexChange={setActiveIndex}
        customCarouselLayoutStyles={classnames({
          'new-carousel-navigation-v2': isMMAV2Enabled,
          'new-carousel-navigation': isAdCreatorEnabled,
          'boost-carousel': isMSANBoostEnabled && !isBoostNewAspectRatioSupportEnabled,
          'boost-carousel-new-support': isMSANBoostEnabled && isBoostNewAspectRatioSupportEnabled,
        })}
        customPlayPauseButtonsStyles={classnames(classes.PlayPauseButtons, 'play-pause-mma-msan')}
        permissions={permissions}
      />
      <div className="disclaimer-info">
        <div className="disclaimer-info-inline">
          {i18n.getString('ImagePreview_Disclaimer_Message_MultimediaAd')}
        </div>
      </div>
      {enableViewAllBtn &&
        <ActionButton
          className="view-placements"
          onRenderIcon={() => (<span className="iconba iconba-FullView" />)}
          onClick={() => {
            const { viewAllPlacementsActivity } = getAdCreatorInstrumentation(instrumentation, adCreatorScenario); // eslint-disable-line max-len

            viewAllPlacementsActivity.trace('UserOpenedViewAllPlacements');
            onUsedViewAllPlacements();
            setIsViewAllPanelOpen(true);
          }}
        >
          {i18n.getString('ImageExperience_View_Placements_Example')}
        </ActionButton>
      }
      {/* This grid preview is another layout of carousel preview */}
      {enableViewAllBtn &&
        <AdPlacementsGridPreviewPanel
          isOpen={isViewAllPanelOpen}
          onDismiss={() => {
            setIsViewAllPanelOpen(false);
          }}
          className={classes.gridPreviewContainer}
          cellsNumberPerRow={2}
          // totalCount={_.size(imageListForDisplay)}
          totalCount={_.size(placementList)}
          onRenderCell={(cellIndex) => {
            const imageAsset = imageListForDisplay[cellIndex];

            return (
              <div className="placements-grid-preview-cell">
                {getAdPreviewHeaderInternal({
                  title: getPlacementTitle(cellIndex, true),
                  index: cellIndex,
                  enableActionBtn: true,
                })}
                {getValidationMessage(imageAsset)}
                {placementList && placementList[cellIndex] ? (
                  <AdPreviewSinglePlacementLayout
                    placementData={placementList[cellIndex]}
                    onRenderPlacement={onRenderPlacementGridView}
                    hasContainer={false}
                  />
                ) : null}
              </div>
            );
          }}
        />
      }
    </div>
  );
};

UnStyledMMAMSANPreviewCarousel.propTypes = {
  adPreviewConfig: PropTypes.shape({
    placementConfig: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  previewTexts: PropTypes.shape({
    longHeadline: PropTypes.string,
    description: PropTypes.string,
    callToAction: PropTypes.string,
    businessName: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  hotspots: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    desc: PropTypes.string,
    actionText: PropTypes.string,
    uri: PropTypes.string,
  })),
  genericImage: PropTypes.string.isRequired,
  imageList: PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceImage)),
  logo: PropTypes.instanceOf(ImageExperienceImage),
  onCropImage: PropTypes.func,
  onAddAspectRatio: PropTypes.func,
  onRemoveAspectRatio: PropTypes.func,
  isAdCreatorEnabled: PropTypes.bool,
  isMSANBoostEnabled: PropTypes.bool,
  enableAutoRotate: PropTypes.bool,
  totalImageListCount: PropTypes.number,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func,
  }).isRequired,
  isOnlyOneIncludedImage: PropTypes.func,
  classes: PropTypes.shape({
    gridPreviewContainer: PropTypes.string,
    PlayPauseButtons: PropTypes.string,
    PlayPauseButtonsContainer: PropTypes.string,
  }),
  disablePlacementActions: PropTypes.bool,
  adCreatorScenario: PropTypes.shape({
    scenarioName: PropTypes.string,
    scenarioValue: PropTypes.string,
  }),
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }),
  onUsedViewAllPlacements: PropTypes.func,
  permissions: PropTypes.shape({}),
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }),
  isMMAV2Enabled: PropTypes.bool,
  isBoost200x100ImageEnabled: PropTypes.bool,
  isBoostNewAspectRatioSupportEnabled: PropTypes.bool,

  isImageFittingEnabled: PropTypes.bool,
};

UnStyledMMAMSANPreviewCarousel.defaultProps = {
  onCropImage: _.noop,
  onAddAspectRatio: _.noop,
  onRemoveAspectRatio: _.noop,
  isOnlyOneIncludedImage: _.noop,
  isAdCreatorEnabled: false,
  logo: null,
  isMSANBoostEnabled: false,
  enableAutoRotate: false,
  totalImageListCount: 0,
  classes: {},
  hotspots: [],
  imageList: [],
  disablePlacementActions: false,
  adCreatorScenario: null,
  instrumentation: {
    addScenario: _.noop,
  },
  onUsedViewAllPlacements: _.noop,
  permissions: {},
  newI18n: {
    getString: _.noop,
  },
  isMMAV2Enabled: false,
  isBoost200x100ImageEnabled: false,
  isBoostNewAspectRatioSupportEnabled: false,

  isImageFittingEnabled: false,
};

export const UnifiedMMAMSANPreviewCarousel =
  withDefaultStyles(UnStyledMMAMSANPreviewCarousel, getPreviewCarouselStyle);
